import React, { useEffect, useState } from "react";
import { IFormTreeProps } from "./IFormTreeProps";
import { fetchFormNode, fetchFormSearchResults, Form } from "../../api/object/Form";
import { getTheme, IScrollablePaneStyles, mergeStyleSets, ScrollablePane } from "@fluentui/react";

import {
  ItemRenderProps,
  processTreeViewItems,
  TreeView,
  TreeViewExpandChangeEvent,
  TreeViewItemClickEvent,
} from "@progress/kendo-react-treeview";
import "@progress/kendo-theme-bootstrap/dist/all.css";
import { SkeletonItem } from "@fluentui/react-components";
import { CoSuspense } from "../../common/loading/CoSuspense";
import { SkeletonLoader } from "../../common/loading/SkeletonLoader";
import { SearchBar } from "../generic/searchBar/SearchBar";
import { NoResultScreen } from "../coobjects/tree-pills-input/SearchQueryAndResults";
import { generateIdentifier } from "../../utils/helpers/RobotFramework";

export interface FormTreeSelection {
  dbId: string;
  formCode: string;
  formDesc: string;
  key: string;
  oidPath: string[];
}

interface TreeState {
  dbIdLoaded: string;
  treeItems: TreeViewDataItem[];
  selectState: string[];
}

interface TreeViewDataItem {
  text: string;
  key: string;
  hasChildren: boolean;
  dbId: string;
  nodeType: "form" | "node" | "skeleton";
  formCode: string;
  formDesc: string;
  expanded?: boolean;
  checked?: boolean;
  selected?: boolean;
  items?: TreeViewDataItem[];
  oidPath?: string[];
}

/**
 * Ultis functions
 */
function mapFormToDataItem(dbId: string, form: Form): TreeViewDataItem {
  const item: TreeViewDataItem = {
    text: form.desc,
    key: form.oid,
    hasChildren: true,
    dbId: dbId,
    nodeType: form.type,
    formCode: form.code,
    formDesc: form.desc,
    expanded: false,
  };
  if (form.type === "form") {
    item.hasChildren = false;
    item.expanded = true;
    item.items = [];
  }
  return item;
}

function mapFormSearchedItemsToDataItem(dbId: string, form: Form): TreeViewDataItem {
  const oidPath = form.oid.split(",");

  const item: TreeViewDataItem = {
    text: form.desc,
    key: oidPath[oidPath.length - 1],
    hasChildren: true,
    dbId: dbId,
    nodeType: form.type,
    formCode: form.code,
    formDesc: form.desc,
    expanded: false,
    oidPath: oidPath,
  };
  if (form.type === "form") {
    item.hasChildren = false;
    item.expanded = true;
    item.items = [];
  }
  return item;
}

function createSkeletonFormItems(parentKey: string, count = 3): TreeViewDataItem[] {
  return Array.from({ length: count }, (_, i) => ({
    text: "",
    key: `__skeleton__-${parentKey}-${i}`,
    hasChildren: false,
    dbId: "",
    nodeType: "skeleton" as TreeViewDataItem["nodeType"],
    formCode: "",
    formDesc: "",
    expanded: false,
  }));
}

export const ItemRenderer = (props: ItemRenderProps) => {
  if (props.item.nodeType === "skeleton") {
    return <SkeletonItem id="skeleton" style={{ height: 20, width: "80%" }} animation="pulse" />;
  }
  return (
    <>
      <span className={iconClassName(props.item)} key="0" />
      <span key="1" aria-label={generateIdentifier(props.item.text, "form_name")}>
        {`${props.item.formCode} - ${props.item.text}`}
      </span>
    </>
  );
};

function iconClassName(item: TreeViewDataItem) {
  if (item.nodeType === "node") {
    return "k-icon k-i-folder";
  } else {
    return "k-icon k-i-excel";
  }
}

export const FormTree: React.FunctionComponent<IFormTreeProps> = (props) => {
  // state hooks
  const [state, setState] = useState<TreeState>({ dbIdLoaded: "", treeItems: [], selectState: [] });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [searchState, setSearchState] = useState<{
    searchQuery: string | undefined;
    results: TreeViewDataItem[] | undefined;
  }>({
    searchQuery: undefined,
    results: undefined,
  });
  const copyItemsAndItem = (
    items: TreeViewDataItem[],
    indexes: number[]
  ): { items: TreeViewDataItem[]; item: TreeViewDataItem } => {
    const copiedItems = items.slice();
    const item = (copiedItems[indexes[0]] = { ...copiedItems[indexes[0]] });

    if (indexes.length > 1) {
      const { items: children, item: target } = copyItemsAndItem(item.items, indexes.slice(1));
      item.items = children;
      return { items: copiedItems, item: target };
    } else {
      return { items: copiedItems, item: item };
    }
  };

  const treePath = (items: TreeViewDataItem[], indexes: number[]): TreeViewDataItem[] => {
    const item = items[indexes[0]];
    if (item.items.length) {
      return [item, ...treePath(item.items, indexes.slice(1))];
    } else {
      return [item];
    }
  };

  const { dbId } = props;

  useEffect(() => {
    setIsInitialLoading(true);
    fetchFormNode(dbId).then((dataForms) => {
      let roots = dataForms.map((form) => mapFormToDataItem(dbId, form));
      if (props.oidPath?.length) {
        // eslint-disable-next-line no-undef
        console.log(`provided oidPath ${props.oidPath} ... `);
        retrieveSelectedData(dbId, props.oidPath, roots)
          .then(({ rootsWithPath, itemHierarchicalIndex }) => {
            setState({
              dbIdLoaded: dbId,
              treeItems: rootsWithPath,
              selectState: [itemHierarchicalIndex],
            });
          })
          .catch((e) => {
            // eslint-disable-next-line no-undef
            console.error(e);
            setState({
              dbIdLoaded: dbId,
              treeItems: roots,
              selectState: [],
            });
          });
      } else {
        setState({
          dbIdLoaded: dbId,
          treeItems: roots,
          selectState: [],
        });
      }
    }).finally(() => setIsInitialLoading(false));
  }, [dbId]);

  const onExpandChange = (event: TreeViewExpandChangeEvent) => {
    const isExpandingSearch = searchState.results !== undefined && searchState.results !== null;
    const treeToElaborate = isExpandingSearch ? searchState.results : state.treeItems;
    const setElaboratedTree = (items: TreeViewDataItem[]) => {
      if (isExpandingSearch) setSearchState((s) => ({ ...s, results: items }));
      else setState((s) => ({ ...s, treeItems: items }));
    };

    const indexes = event.itemHierarchicalIndex.split("_").map((value) => Number(value));
    const { items, item } = copyItemsAndItem(treeToElaborate, indexes);
    item.expanded = !item.expanded;
    if (item.items) {
      setElaboratedTree(items);
    } else {
      item.items = createSkeletonFormItems(item.key);
      setElaboratedTree(items);

      fetchFormNode(dbId, item.key).then((dataForms) => {
        const { items: children, item: parent } = copyItemsAndItem(items, indexes);
        parent.items = dataForms.map((form) => {
          if (isExpandingSearch)
            return mapFormSearchedItemsToDataItem(dbId, { ...form, oid: [...parent.oidPath, form.oid].join(",") });
          return mapFormToDataItem(dbId, form);
        });
        parent.hasChildren = false; // hasChildren is need only for lazy loading
        setElaboratedTree(children);
      }).catch(() => {
        const { items: errorItems, item: errorParent } = copyItemsAndItem(items, indexes);
        errorParent.expanded = false;
        errorParent.items = undefined;
        setElaboratedTree(errorItems);
      });
    }
  };

  const onItemClick = (event: TreeViewItemClickEvent) => {
    if (event.item.nodeType === "skeleton") return;
    if (event.item.nodeType === "form") {
      setState({ ...state, selectState: [event.itemHierarchicalIndex] });

      if (props.onFormClick) {
        props.onFormClick({
          dbId: event.item.dbId,
          formCode: event.item.formCode,
          formDesc: event.item.formDesc,
          key: event.item.key,
          oidPath:
            event.item.oidPath ??
            treePath(
              state.treeItems,
              event.itemHierarchicalIndex.split("_").map((value) => Number(value))
            ).map((item) => item.key),
        });
      }
    }
  };

  let timer;

  function handleChangeSearch(query: string) {
    // eslint-disable-next-line no-undef
    clearTimeout(timer);
    // eslint-disable-next-line no-undef
    timer = setTimeout(() => {
      setSearchState((s) => ({
        ...s,
        searchQuery: query,
      }));
    }, 1000);
  }

  const onSearchStringChange = (searchString: string | undefined) =>
    new Promise<void>((resolve) => {
      if (searchString?.length >= 3) {
        fetchFormSearchResults(dbId, searchString).then((dataForms) => {
          const roots = dataForms.map((form) => mapFormSearchedItemsToDataItem(dbId, form));
          setSearchState((s) => ({ ...s, results: roots }));
          resolve();
        });
      }
      setSearchState((s) => ({ ...s, results: undefined }));
    });

  useEffect(() => {
    onSearchStringChange(searchState.searchQuery);
  }, [searchState.searchQuery]);

  return (
    <div>
      <SearchBar onSearch={handleChangeSearch} />
      {searchState.results?.length === 0 && <NoResultScreen isFormTree={true} />}
      <CoSuspense isLoading={isInitialLoading} loadingComponent={<SkeletonLoader />}>
        {state.dbIdLoaded === dbId ? (
          <TreeView
            data={processTreeViewItems(searchState.results ?? state.treeItems, {
              select: state.selectState,
            })}
            expandIcons={true}
            onExpandChange={onExpandChange}
            item={ItemRenderer}
            onItemClick={onItemClick}
          />
        ) : null}
      </CoSuspense>
    </div>
  );
};

export const ScrollableFormTree: React.FunctionComponent<IFormTreeProps> = (props) => {
  const theme = getTheme();
  const classNames = mergeStyleSets({
    wrapper: {
      height: "70vh",
      position: "relative",
      maxHeight: "inherit",
    },
    pane: {
      // maxWidth: 400,
      border: "1px solid " + theme.palette.neutralLight,
    },
  });
  const scrollablePaneStyles: Partial<IScrollablePaneStyles> = { root: classNames.pane };

  return (
    <div className={classNames.wrapper}>
      <ScrollablePane styles={scrollablePaneStyles}>
        <FormTree {...props} />
      </ScrollablePane>
    </div>
  );
};

function retrieveSelectedData(
  dbId: string,
  oidPath: string[],
  roots: TreeViewDataItem[]
): Promise<{ rootsWithPath: TreeViewDataItem[]; itemHierarchicalIndex: string }> {
  const data: Map<string, Form[]> = new Map();
  const nodeOidPath = oidPath.slice(0, oidPath.length - 1);
  const formOid = oidPath[oidPath.length - 1];
  const promises: Promise<Form[]>[] = nodeOidPath.map((oid) => {
    const promise = fetchFormNode(dbId, oid);
    promise.then((forms) => data.set(oid, forms));
    return promise;
  });
  return Promise.all(promises).then(() => {
    let items = roots;
    const hierarchicalIndexArray: number[] = [];
    nodeOidPath.forEach((oid) => {
      const children = data.get(oid).map((form) => mapFormToDataItem(dbId, form));
      const fatherIndex = items.findIndex((value) => value.key === oid);
      if (fatherIndex === -1) {
        throw new Error("The selected form's father could not be retrieved");
      }
      const father = items[fatherIndex];
      hierarchicalIndexArray.push(fatherIndex);
      father.expanded = true;
      father.items = children;
      items = children;
    });

    // getting form index
    const formIndex = items.findIndex((value) => value.key === formOid);
    if (formIndex === -1) {
      throw new Error("The selected form could not be found in the father");
    }
    hierarchicalIndexArray.push(formIndex);
    return { rootsWithPath: roots, itemHierarchicalIndex: hierarchicalIndexArray.join("_") };
  });
}
